/**
 * Laço único de interpolação numérica (§3.6.4 regra 3): um só requestAnimationFrame dirige todas as instâncias de
 * AnimatedNumber e para quando não há interpolação ativa. Nada é agendado com a aba oculta (AC-3.6-04); quando a
 * preferência de movimento reduzido vira true ou a aba fica oculta, todas as interpolações concluem no valor final
 * no mesmo quadro (§15.6.1 itens 1 e 2).
 */
import { reducedMotion } from '../a11y/useReducedMotion';

export type Ease = (t: number) => number;

/** Curva de A04 (§3.6.3): quase linear, p = 0,7t + 0,3·(3t² − 2t³); estritamente crescente em [0, 1]. */
export const easeLive: Ease = (t) => 0.7 * t + 0.3 * (3 * t * t - 2 * t * t * t);

/** Ease-out cúbico da intro (A02/A04). */
export const easeOutCubic: Ease = (t) => 1 - (1 - t) ** 3;

/** Abaixo disso não há interpolação contínua (§3.6.2): o número salta. */
export const SNAP_MS = 300;
/** Valor de --dur-live-tween antes do primeiro snapshot (tokens.css, §3.6.2). */
export const DEFAULT_LIVE_TWEEN_MS = 6000;

export interface TweenOptions {
  from: number;
  to: number;
  /** ms */
  duration: number;
  /** ms antes de começar a andar; durante o atraso o valor exibido é `from`. */
  delay?: number;
  ease?: Ease;
  /** Recebe cada valor exibido, em ordem monotônica de `from` a `to`; o último é exatamente `to`. */
  onUpdate: (value: number) => void;
  onDone?: () => void;
}

export interface TweenHandle {
  /** Interrompe sem pintar o valor final. */
  cancel(): void;
  /** Pinta o valor final agora e encerra. */
  finish(): void;
  readonly active: boolean;
}

interface Job {
  from: number;
  to: number;
  start: number;
  delay: number;
  duration: number;
  ease: Ease;
  lastP: number;
  onUpdate: (value: number) => void;
  onDone: (() => void) | undefined;
}

const jobs = new Set<Job>();
let rafId: number | null = null;
let installed = false;

const now = (): number => performance.now();
const tabHidden = (): boolean => typeof document !== 'undefined' && document.hidden;

/** Aba oculta (document.hidden). */
export function isTabHidden(): boolean {
  return tabHidden();
}

function install(): void {
  if (installed) return;
  installed = true;
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) finishAllTweens();
      else ensureLoop();
    });
  }
  reducedMotion.subscribe((reduce) => {
    if (reduce) finishAllTweens();
  });
}

/** Valor da curva no progresso p, sem passar do alvo (AC-3.8-29). */
function valueAt(job: Job, p: number): number {
  if (p >= 1) return job.to;
  const v = job.from + (job.to - job.from) * p;
  return job.to >= job.from ? Math.min(v, job.to) : Math.max(v, job.to);
}

function complete(job: Job): void {
  jobs.delete(job);
  job.onUpdate(job.to);
  job.onDone?.();
}

function stopLoop(): void {
  if (rafId !== null && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(rafId);
  rafId = null;
}

function ensureLoop(): void {
  if (rafId !== null || jobs.size === 0 || tabHidden()) return;
  rafId = requestAnimationFrame(frame);
}

function frame(): void {
  rafId = null;
  if (tabHidden() || reducedMotion.value) {
    finishAllTweens();
    return;
  }
  const t = now();
  for (const job of [...jobs]) {
    const elapsed = t - job.start - job.delay;
    if (elapsed < 0) continue;
    const linear = job.duration > 0 ? Math.min(1, elapsed / job.duration) : 1;
    if (linear >= 1) {
      complete(job);
      continue;
    }
    // Progresso nunca recua, mesmo que o relógio oscile: a sequência exibida é monotônica.
    const p = Math.max(job.lastP, Math.min(1, Math.max(0, job.ease(linear))));
    job.lastP = p;
    job.onUpdate(valueAt(job, p));
  }
  ensureLoop();
}

/**
 * Inicia uma interpolação no laço compartilhado. Pinta `from` na hora. Sem duração, com movimento reduzido ou com a
 * aba oculta, pinta `to` direto e não agenda quadro algum.
 */
export function tween(opts: TweenOptions): TweenHandle {
  install();
  const job: Job = {
    from: opts.from,
    to: opts.to,
    start: now(),
    delay: Math.max(0, opts.delay ?? 0),
    duration: Math.max(0, opts.duration),
    ease: opts.ease ?? easeLive,
    lastP: 0,
    onUpdate: opts.onUpdate,
    onDone: opts.onDone,
  };
  const handle: TweenHandle = {
    cancel() {
      jobs.delete(job);
      if (jobs.size === 0) stopLoop();
    },
    finish() {
      if (jobs.has(job)) complete(job);
      if (jobs.size === 0) stopLoop();
    },
    get active() {
      return jobs.has(job);
    },
  };
  const instant = job.duration <= 0 || job.from === job.to || reducedMotion.value || tabHidden();
  if (instant) {
    job.onUpdate(job.to);
    job.onDone?.();
    return handle;
  }
  jobs.add(job);
  job.onUpdate(job.from);
  ensureLoop();
  return handle;
}

/** Conclui todas as interpolações no valor final, no mesmo quadro, e para o laço (§15.6.1). */
export function finishAllTweens(): void {
  stopLoop();
  for (const job of [...jobs]) complete(job);
}

/** Quantas interpolações estão em curso (diagnóstico e testes). */
export function activeTweens(): number {
  return jobs.size;
}

/** "6s" → 6000; "12750ms" → 12750; vazio ou inválido → null. */
export function parseDuration(raw: string): number | null {
  const m = /^\s*(-?\d*\.?\d+)\s*(ms|s)\s*$/i.exec(raw);
  if (!m) return null;
  const n = Number(m[1]);
  if (!Number.isFinite(n)) return null;
  return m[2]?.toLowerCase() === 's' ? n * 1000 : n;
}

/**
 * Duração da interpolação ao vivo (DE-13): --dur-live-tween do :root, escrito por useTweenDuration() (§3.6.2) no
 * estilo inline; sem ele, o valor de tokens.css; sem nenhum, 6 s.
 */
export function liveTweenMs(): number {
  if (typeof document === 'undefined') return DEFAULT_LIVE_TWEEN_MS;
  const root = document.documentElement;
  const inline = parseDuration(root.style.getPropertyValue('--dur-live-tween'));
  if (inline !== null) return inline;
  const computed =
    typeof getComputedStyle === 'function'
      ? parseDuration(getComputedStyle(root).getPropertyValue('--dur-live-tween'))
      : null;
  return computed ?? DEFAULT_LIVE_TWEEN_MS;
}
